/**
 * Authenticated app shell — Icarus D&D topbar (desktop + tablet) +
 * bottom-nav (mobile). Redesigned from the previous fixed sidebar; see
 * `Topbar.tsx` for why nothing that used to live in the sidebar
 * (org switcher, admin/settings, theme toggle, legal links, logout) was
 * dropped — it now lives in the topbar's avatar menu.
 *
 * The mobile bottom-nav is unchanged from before this redesign; it
 * co-exists with the topbar exactly as it did with the sidebar.
 */

import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { WifiOff, Play, X as XClose } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { messagesRepo } from '@/data';
import { CommandPalette } from '@/components/CommandPalette';
import { KeyboardShortcutsModal } from '@/components/KeyboardShortcutsModal';
import { Topbar } from '@/components/Topbar';
import { SupportModeBanner } from '@/components/SupportModeBanner';
import { FloatingRecordingBar } from '@/components/FloatingRecordingBar';
import { NutriAssistant } from '@/components/nutri-assistant';
import { MobileBottomNav } from '@/components/MobileBottomNav';

/** Subscribe to the browser's online/offline events. */
function useOnlineStatus(): boolean {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);
  return isOffline;
}

const LayoutShell = () => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { isDemo, exitDemo } = useDemo();
  const navigate = useNavigate();
  const orgId = profile?.organization_id;
  const isOffline = useOnlineStatus();
  const isPlatformAdminWorkspace = profile?.role === 'admin';
  // Drives MobileBottomNav's own `MoreSheet` (unrelated to the old sidebar
  // drawer, which this redesign removes entirely).
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  // Unread message count for the mobile bottom-nav badge.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unread-messages', orgId],
    enabled: !!orgId,
    queryFn: () => messagesRepo.countUnreadInbound(orgId!),
    refetchInterval: 20_000,
  });

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      <Topbar />

      <CommandPalette />
      <KeyboardShortcutsModal />

      <main
        id="main"
        tabIndex={-1}
        className="flex-1 overflow-y-auto pb-16 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-inset lg:pb-0"
        role="main"
      >
        <SupportModeBanner />
        {isDemo && (
          <div className="flex items-center justify-between border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            <div className="flex items-center gap-2">
              <Play className="h-3.5 w-3.5" />
              <span className="font-medium">{t('nav.demoBanner')}</span>
              <span className="hidden text-amber-600 sm:inline">{t('nav.demoBannerHint')}</span>
            </div>
            <button
              onClick={() => {
                exitDemo();
                navigate('/');
              }}
              className="flex items-center gap-1 rounded-md px-2 py-1 font-medium transition-colors hover:bg-amber-100"
            >
              <XClose className="h-3 w-3" />
              {t('nav.demoExit')}
            </button>
          </div>
        )}
        {isOffline && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            <WifiOff className="h-3.5 w-3.5" />
            <span className="font-medium">{t('nav.offlineBanner')}</span>
            <span className="hidden text-amber-600 sm:inline">{t('nav.offlineHint')}</span>
          </div>
        )}
        <Outlet />
      </main>

      <FloatingRecordingBar />
      <NutriAssistant />

      <MobileBottomNav
        isPlatformAdminWorkspace={isPlatformAdminWorkspace}
        unreadCount={unreadCount}
        navOpen={mobileMoreOpen}
        onNavOpenChange={setMobileMoreOpen}
      />
    </div>
  );
};

export const Layout = LayoutShell;
